import React, { useState } from "react";
import { RosetteForm } from "@/components/form/themed/RosetteForm.js";
import { SubmitButton } from "@/components/form/SubmitButton.js";
import { OrSeparator } from "@rosette/web_components/src/generic/OrSeparator.js";
import { SocialSignInButtons } from "@/components/auth/SocialSignInButtons.js";
import { TestUserLoginButtons } from "@/components/auth/TestUserLoginButtons.js";
import Link from "next/link";
import { PasswordInput } from "@rosette/web_components/src/form/input/PasswordInput.js";
import { useSearchParams } from "next/navigation";
import { Modal } from "@rosette/web_components/src/ui/Modal.js";
import type { ArticleDirPiece } from "@/components/articles/ArticleDir.js";
import { useWindow } from "@rosette/web_utils/src/ssrHelper.js";
import { useOptionalEnv } from "@/components/env/EnvContext.js";
import { Label } from "@rosette/web_components/src/ui/Label.js";
import { TextInput } from "@rosette/web_components/src/ui/TextInput.js";


interface LoginFormProps {
    backPieces?: ArticleDirPiece[];
    environment: string;
}


export function LoginForm({ backPieces, environment }: LoginFormProps) {
    const searchParams = useSearchParams();
    let redirectPathname = searchParams.get("redirect");

    const theWindow = useWindow();
    if (redirectPathname === null && theWindow) {
        // Used for login modal.
        if (theWindow.location.pathname !== "/login") {
            redirectPathname = theWindow.location.pathname + theWindow.location.search;
        }
    }

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const emailError = (email.length === 0 ? "Please provide an email" : null);
    const passwordError = (password.length === 0 ? "Please provide a password" : null);

    const validationError = emailError ?? passwordError;

    return (
        <RosetteForm
            title="Sign In to RoyalUr.net"
            action="/login/password"
            validationError={validationError}
            backPieces={backPieces}>

            <SocialSignInButtons redirectPathname={redirectPathname} />
            <OrSeparator />

            <Label htmlFor="email">Email</Label>
            <TextInput
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required={true} />

            <Label htmlFor="password">Password</Label>
            <PasswordInput
                id="password"
                name="password"
                value={password}
                setValue={setPassword}
                required={true} />

            {redirectPathname && (
                <input
                    type="hidden"
                    id="redirectPathname"
                    name="redirectPathname"
                    value={redirectPathname} />
            )}

            <p className="mt-1 mb-2 block w-full text-right text-sm">
                <Link href="/recover">
                    Forgot password?
                </Link>
            </p>

            <SubmitButton tone="rosette" label="Sign In" />

            <p>
                Don&apos;t have an account?
                {" "}
                <Link href="/register">
                    Register here.
                </Link>
            </p>

            {environment !== "prod" && (
                <>
                    <OrSeparator />
                    <TestUserLoginButtons redirectPathname={redirectPathname} />
                </>
            )}
        </RosetteForm>
    );
}


interface LoginFormModalProps {
    visible: boolean;
    close: () => void;
}


export function LoginFormModal({ visible, close }: LoginFormModalProps) {
    const env = useOptionalEnv();
    if (!env)
        return null;

    return (
        <Modal open={visible} onClose={close}>

            <LoginForm environment={env.deploymentEnv} />
        </Modal>
    );
}
